import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { ShipmentCustomNameText } from "components/Texts/ShipmentCustomNameText";
import { ShipmentEquipmentTypeText } from "components/Texts/ShipmentEquipmentTypeText";
import { ShipmentFootageText } from "components/Texts/ShipmentFootageText";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import { ShipmentWeightText } from "components/Texts/ShipmentWeightText";
import {
  convertSubdivisionCodeToSimplifiedSubdivisionCode,
  formatText,
  getServiceTypeDisplayLabel,
} from "utils";

import { EVOTRUX_TEAL } from "../../../../../constants";

interface Props {
  supplierShipment: SupplierShipment;
}

export function SupplierShipmentPreview(props: Props) {
  const navigate = useNavigate();
  const { supplierShipment } = props;
  const shipmentInfo = supplierShipment.info;
  const firstPointLocation = supplierShipment?.first_point?.point_location;
  const lastPointLocation = supplierShipment?.last_point?.point_location;

  return (
    <Box
      display="flex"
      px="2rem"
      py="1rem"
      sx={{
        "&:hover": {
          bgcolor: "#f8f8f8",
          cursor: "pointer",
        },
        // Teal left border accent for supplier shipments
        borderLeft: `4px solid ${EVOTRUX_TEAL}`,
      }}
      onClick={() => {
        navigate(`/supplier-shipments/${shipmentInfo.id}`);
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
          <Stack spacing={1}>
            <ShipmentEquipmentTypeText data={supplierShipment?.equipments} />
            <Typography sx={{ textTransform: "capitalize" }}>
              {getServiceTypeDisplayLabel(shipmentInfo.service)}
            </Typography>
            <ShipmentWeightText data={supplierShipment?.info?.weight_lb} />
            <Typography variant="body2" color="textSecondary">
              Shipment ID: {shipmentInfo.id}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: EVOTRUX_TEAL, fontWeight: "bold" }}
            >
              From: {supplierShipment.supplier_company.name}
            </Typography>
            {supplierShipment?.info.custom_name != null && (
              <ShipmentCustomNameText
                data={supplierShipment.info.custom_name}
              />
            )}
          </Stack>
        </Grid>
        <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
          <Box ml="2rem" flex={1} alignSelf="center">
            <Typography sx={{ textTransform: "capitalize" }} variant="body1">
              {formatText(shipmentInfo.commodity_freetext ?? "General Freight")}
            </Typography>

            <Stack my="0.5rem" direction="row" alignItems="center">
              <ShipmentLocationText
                data={`${firstPointLocation?.city}, ${firstPointLocation?.province ? convertSubdivisionCodeToSimplifiedSubdivisionCode(firstPointLocation.province) : ""}`}
                icon={
                  <TripOriginIcon
                    sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                  />
                }
              />
              <Box flex={1} mx="1rem">
                <Divider />
              </Box>
              <ShipmentLocationText
                data={`${lastPointLocation?.city}, ${lastPointLocation?.province ? convertSubdivisionCodeToSimplifiedSubdivisionCode(lastPointLocation.province) : ""}`}
                icon={
                  <FmdGoodIcon
                    sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                  />
                }
              />
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={4}
            mx="2rem"
            justifySelf="flex-end"
            alignSelf="center"
          >
            <ShipmentFootageText data={supplierShipment?.info?.footage} />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            mx="2rem"
            mt="0.5rem"
            alignItems="center"
          >
            <ShipmentStatusBadge
              status={supplierShipment?.shipment_status}
              size="medium"
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
